import React, { useState } from 'react';
import { Button, useMediaQuery } from '@mui/material';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import { useHistory, useLocation } from 'react-router-dom';
import camelcaseKeys from 'camelcase-keys';
import { useTranslation } from 'react-i18next';
import { checkVietNam } from '@src/utils/checkCountry';
import CustomOptions from '@src/components/CustomOptions';
import percentIcon from '@src/assets/icons/percentIcon.svg';
import { formatNumber } from '@src/utils/number';
import { LANGUAGE } from '@src/constants';

import theme from '@src/styles/theme';
import {
  calculateResourcePrice,
  generateSelectOptions,
  roundSaleOffPercentage,
} from '@src/services/package';
import { RESOURCES_TYPE } from '@src/constants/package';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useIpLocation from '@src/hooks/useLocation';
import {
  Container,
  Label,
  Row,
  TagDiscount,
  FormGroup,
} from './packageInfo.style';

const CustomResources = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isVietNam = checkVietNam();
  const { language } = i18n;
  const { package: packageState } = useSelector((state) => state.order);
  const { ip } = useIpLocation();
  const { getFeatureValue } = useFeatureFlags();
  const mergePackageApiToStudioEnabled = getFeatureValue(
    FEATURE_KEYS.MERGE_PACKAGE_API_TO_STUDIO,
    {
      ip,
    },
  );

  const [openPopup, setOpenPopup] = useState(false);
  const [tooltipDetail, setTooltipDetail] = useState('');

  const { customResource, note } = packageState || {};
  const {
    credits: creditsResource,
    devices: devicesResource,
    voiceClonings: voiceCloningsResource,
    concurrentRequestApi: concurrentRequestApiResource,
  } = customResource || {};

  const {
    autoRenew,
    voucherCode: voucherCodeParamURL,
    bannerType,
    package: packageCode,
    credits: creditsQuery,
    devices: devicesQuery,
    voiceClonings: voiceCloningsQuery,
    concurrentRequestApi: concurrentRequestApiQuery,
  } = camelcaseKeys(queryString.parse(location.search) || {});

  const updateRoute = (
    credits,
    devices,
    voiceClonings,
    concurrentRequestApi,
  ) => {
    const query = queryString.stringify({
      autoRenew,
      voucherCode: voucherCodeParamURL,
      bannerType,
      package: packageCode,
      ...(credits && { credits }),
      devices: devices || 0,
      voiceClonings: voiceClonings || 0,
      ...(concurrentRequestApi !== undefined && {
        concurrentRequestApi: concurrentRequestApi || 0,
      }),
    });
    history.replace({ search: query });
  };

  const handleChangeCredits = (option) =>
    option &&
    updateRoute(
      option.value,
      devicesQuery,
      voiceCloningsQuery,
      concurrentRequestApiQuery,
    );

  const handleChangeDevices = (option) =>
    option &&
    updateRoute(
      creditsQuery,
      option.value,
      voiceCloningsQuery,
      concurrentRequestApiQuery,
    );

  const handleChangeVoices = (option) =>
    option &&
    updateRoute(
      creditsQuery,
      devicesQuery,
      option.value,
      concurrentRequestApiQuery,
    );

  const handleChangeConcurrentRequestApi = (option) =>
    option &&
    updateRoute(creditsQuery, devicesQuery, voiceCloningsQuery, option.value);

  const handleShowPopup = (tooltip) => {
    setTooltipDetail(tooltip);
    setOpenPopup(true);
  };

  const formatPrice = (vndPrice, usdPrice) => {
    if (isVietNam) {
      const unitPrice = "<span style='text-decoration: underline;'>đ</span>";
      return `${unitPrice}${vndPrice?.toLocaleString(language)}`;
    }
    return `$${usdPrice?.toLocaleString(language)}`;
  };

  const getPricePerUnit = (prices, currentValue) => {
    const pricePerUnit = prices?.find(
      (price) => currentValue >= 0 && currentValue <= price?.max,
    );
    return {
      vndPricePerUnit: pricePerUnit?.vndPricePerUnit,
      usdPricePerUnit: pricePerUnit?.usdPricePerUnit,
    };
  };

  const getDiscountPercentageCredits = () => {
    const creditsPrice = calculateResourcePrice({
      value: +creditsQuery || customResource?.credits?.min,
      pricingPlans: customResource?.credits,
    });
    const price = isVietNam ? creditsPrice?.vndPrice : creditsPrice?.usdPrice;
    const originalPrice = isVietNam
      ? creditsPrice?.originalVndPrice
      : creditsPrice?.originalUsdPrice;
    const saleOffPercentage = (1 - price / originalPrice) * 100;
    return roundSaleOffPercentage(saleOffPercentage);
  };

  const CustomCredits = () => {
    const { prices, step, min, title, description, tooltip } =
      creditsResource || {};
    const options = generateSelectOptions({
      prices,
      step,
      resource: RESOURCES_TYPE.CREDITS,
    });
    const credits = Number(creditsQuery) || min;

    return (
      <CustomOptions
        dataId="option-custom-credits-detail"
        dataKey="credits"
        headerOption={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Label style={{ marginBottom: 0 }}>{title?.[language]}</Label>
            {Boolean(getDiscountPercentageCredits()) && (
              <TagDiscount style={{ marginBottom: 0 }}>
                <img
                  src={percentIcon}
                  className="percent-icon"
                  alt="percentIcon"
                />
                {t('percentDiscount', {
                  percentDiscount: getDiscountPercentageCredits(),
                })}
              </TagDiscount>
            )}
          </div>
        }
        infoOption={description?.[language]}
        value={{
          label: formatNumber(credits, language),
          value: credits,
        }}
        onChange={handleChangeCredits}
        detailInfoOption={
          !!tooltip?.[language] && (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(t(tooltip?.[language]), {
                  allowedTags: ['ul', 'li'],
                  allowedAttributes: {
                    li: ['style'],
                    ul: ['style'],
                  },
                }),
              }}
            />
          )
        }
        options={options}
        inputType="number"
        handleClickShowPopup={() => handleShowPopup(tooltip?.[language])}
        showInfoOptionBottom={false}
        showInfoOptionTop
        note={
          note && (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  language === LANGUAGE.VN ? note[language] : note[LANGUAGE.EN],
                ),
              }}
            />
          )
        }
      />
    );
  };

  const CustomDevices = () => {
    const { prices, step, title, description, tooltip } = devicesResource || {};
    const options = generateSelectOptions({
      prices,
      step,
      resource: RESOURCES_TYPE.DEVICES,
    });
    const devices = Number(devicesQuery) || 0;
    const pricePerUnit = getPricePerUnit(prices, devices);

    return (
      <CustomOptions
        dataId="option-custom-devices-detail"
        dataKey="devices"
        headerOption={<Label>{title?.[language]}</Label>}
        infoOption={
          !!description && (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t(description[language], {
                    pricePerUnit: formatPrice(
                      pricePerUnit?.vndPricePerUnit,
                      pricePerUnit?.usdPricePerUnit,
                    ),
                  }),
                  {
                    allowedTags: ['span'],
                    allowedAttributes: {
                      span: ['style'],
                    },
                  },
                ),
              }}
            />
          )
        }
        value={{
          label: formatNumber(devices, language),
          value: devices,
        }}
        onChange={handleChangeDevices}
        options={options}
        inputType="number"
        handleClickShowPopup={() => handleShowPopup(tooltip?.[language])}
        showInfoOptionBottom
        showInfoOptionTop={false}
      />
    );
  };

  const CustomVoiceClonings = () => {
    const { prices, step, title, description, tooltip } =
      voiceCloningsResource || {};
    const options = generateSelectOptions({
      prices,
      step,
      resource: RESOURCES_TYPE.VOICE_CLONINGS,
    });
    const voiceClonings = Number(voiceCloningsQuery) || 0;
    const pricePerUnit = getPricePerUnit(prices, voiceClonings);

    return (
      <CustomOptions
        dataId="option-custom-voice-clonings-detail"
        dataKey="voiceClonings"
        headerOption={<Label>{title?.[language]}</Label>}
        infoOption={
          !!description && (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t(description[language], {
                    pricePerUnit: formatPrice(
                      pricePerUnit?.vndPricePerUnit,
                      pricePerUnit?.usdPricePerUnit,
                    ),
                  }),
                  {
                    allowedTags: ['span'],
                    allowedAttributes: {
                      span: ['style'],
                    },
                  },
                ),
              }}
            />
          )
        }
        value={{
          label: formatNumber(voiceClonings, language),
          value: voiceClonings,
        }}
        onChange={handleChangeVoices}
        options={options}
        inputType="number"
        handleClickShowPopup={() => handleShowPopup(tooltip?.[language])}
        showInfoOptionBottom
        showInfoOptionTop={false}
      />
    );
  };

  const CustomConcurrentRequestApi = () => {
    if (!concurrentRequestApiResource) return null;

    const { prices, step, title, description, tooltip } =
      concurrentRequestApiResource || {};
    const options = generateSelectOptions({
      prices,
      step,
      resource: RESOURCES_TYPE.CONCURRENT_REQUEST_API,
    });
    const concurrentRequestApi = Number(concurrentRequestApiQuery) || 0;
    const pricePerUnit = getPricePerUnit(prices, concurrentRequestApi);

    return (
      <CustomOptions
        dataId="option-custom-concurrent-request-api-detail"
        dataKey="concurrentRequestApi"
        headerOption={<Label>{title?.[language]}</Label>}
        infoOption={
          !!description && (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t(description[language], {
                    pricePerUnit: formatPrice(
                      pricePerUnit?.vndPricePerUnit,
                      pricePerUnit?.usdPricePerUnit,
                    ),
                  }),
                  {
                    allowedTags: ['span'],
                    allowedAttributes: {
                      span: ['style'],
                    },
                  },
                ),
              }}
            />
          )
        }
        value={{
          label: formatNumber(concurrentRequestApi, language),
          value: concurrentRequestApi,
        }}
        onChange={handleChangeConcurrentRequestApi}
        options={options}
        inputType="number"
        handleClickShowPopup={() => handleShowPopup(tooltip?.[language])}
        showInfoOptionBottom
        showInfoOptionTop={false}
      />
    );
  };

  const PopupCustomResources = () => (
    <NotificationDialogV3
      name={t('detailedInformation')}
      title={t('detailedInformation')}
      description={sanitizeHtml(tooltipDetail, {
        allowedTags: ['ul', 'li'],
        allowedAttributes: {
          li: ['style'],
          ul: ['style'],
        },
      })}
      fitWidth
      open={openPopup && isMobile}
      onClose={() => setOpenPopup(false)}
      variant="null"
      actionComponent={
        <Button
          onClick={() => setOpenPopup(false)}
          className="btn-close-popup"
          variant="contained"
        >
          {t('gotIt')}
        </Button>
      }
    />
  );

  return (
    <Container>
      <Row>
        <FormGroup style={{ flex: 1, marginBottom: 0 }}>
          <CustomCredits />
        </FormGroup>
        {mergePackageApiToStudioEnabled && (
          <FormGroup style={{ flex: 1, marginBottom: 0 }}>
            <CustomDevices />
          </FormGroup>
        )}
      </Row>

      <Row>
        {!mergePackageApiToStudioEnabled && (
          <FormGroup style={{ flex: 1, marginBottom: 0 }}>
            <CustomDevices />
          </FormGroup>
        )}
        <FormGroup style={{ flex: 1, marginBottom: 0 }}>
          <CustomVoiceClonings />
        </FormGroup>
        {mergePackageApiToStudioEnabled && concurrentRequestApiResource && (
          <FormGroup style={{ flex: 1, marginBottom: 0 }}>
            <CustomConcurrentRequestApi />
          </FormGroup>
        )}
      </Row>
      <PopupCustomResources />
    </Container>
  );
};

export default CustomResources;
